import { X, Trash2, RotateCcw, EyeOff, Eye, ArrowUpLeft, ChevronsUp, ChevronUp, ChevronDown, ChevronsDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ImageField } from "@/components/editor/BlockProps";
import { TabsField } from "@/components/editor/TabsField";

const toHex = (c) => {
  const m = (c || "").match(/\d+(\.\d+)?/g);
  if (!m || m.length < 3 || (m.length === 4 && Number(m[3]) === 0)) return "";
  return `#${m.slice(0, 3).map((x) => Number(x).toString(16).padStart(2, "0")).join("")}`;
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wide text-[#555]">{label}</Label>
    {children}
  </div>
);

const ColorField = ({ value, fallback, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value || toHex(fallback) || "#ffffff"} onChange={(e) => onChange(e.target.value)}
      className="w-10 h-9 border border-[#D9D9D6] p-0.5 bg-white cursor-pointer" data-testid={testid} />
    <span className="text-xs text-[#888B8D] flex-1">{value || "Original"}</span>
    {value && <button type="button" onClick={() => onChange(undefined)} className="text-xs underline text-[#555]">zurück</button>}
  </div>
);

const LAYERS = [["front", ChevronsUp, "Ganz nach vorne"], ["up", ChevronUp, "Eine Ebene vor"], ["down", ChevronDown, "Eine Ebene zurück"], ["back", ChevronsDown, "Ganz nach hinten"]];

const LayerField = ({ layer, onLayer }) => (
  <Field label={`Ebene (aktuell ${layer})`}>
    <div className="grid grid-cols-2 gap-1" data-testid="element-layer-controls">
      {LAYERS.map(([act, Icon, l]) => (
        <button key={act} type="button" onClick={() => onLayer(act)}
          className="py-1.5 text-xs font-bold border border-[#D9D9D6] hover:border-black hover:bg-[#EFDF00] transition-colors duration-150 flex items-center justify-center gap-1"
          data-testid={`element-layer-${act}`}><Icon className="w-3.5 h-3.5" /> {l}</button>
      ))}
    </div>
  </Field>
);

export const ElementPanel = ({ info, ov, added, layer, onLayer, groupSize, onUngroup, onChange, onAddedChange, onDelete, onReset, onParent, onClose }) => {
  const set = (k, v) => onChange({ [k]: v });
  const isTabs = added?.kind === "tabs";
  const isText = added ? !["image", "tabs"].includes(added.kind) : info.text !== null;
  const isImg = added ? added.kind === "image" : info.isImg;
  const hasLink = added ? added.kind === "button" : info.href !== null;
  const val = (k, fb) => (added ? added[k] ?? "" : ov[k] ?? fb ?? "");
  const upd = (k, v) => (added ? onAddedChange({ [k]: v }) : set(k, v));

  return (
    <aside className="w-[340px] bg-white border-l border-[#D9D9D6] flex flex-col min-h-0 flex-shrink-0" data-testid="element-panel">
      <div className="flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5]">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-[#888B8D]">Eigenschaften</p>
          <p className="font-bold text-sm truncate" data-testid="element-panel-label">{added ? `Neues Element (${added.kind})` : info.label}</p>
        </div>
        <button type="button" onClick={onClose} className="p-1 hover:bg-[#F2F2F2]" aria-label="Schließen" data-testid="element-panel-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-4 overflow-y-auto flex-1">
        {groupSize > 1 && (
          <div className="flex items-center justify-between bg-[#FFF3E5] border border-[#FF8A00] px-3 py-2" data-testid="element-group-info">
            <span className="text-xs font-bold">Gruppe · {groupSize} Elemente</span>
            <button type="button" onClick={onUngroup} className="text-xs underline" data-testid="element-ungroup-btn">Gruppe lösen</button>
          </div>
        )}
        {isText && (
          <Field label="Text">
            <Textarea rows={3} value={val("text", info.text)} onChange={(e) => upd("text", e.target.value)} data-testid="element-text-input" />
          </Field>
        )}
        {isTabs && <Field label="Reiter"><TabsField value={added.text} onChange={(v) => upd("text", v)} /></Field>}
        {isImg && <Field label="Bild"><ImageField value={val("src", info.src)} onChange={(v) => upd("src", v)} testid="element-src-input" /></Field>}
        {hasLink && (
          <Field label="Link-Ziel (z.B. /termin oder https://...)">
            <Input value={val("href", info.href)} onChange={(e) => upd("href", e.target.value)} data-testid="element-href-input" />
          </Field>
        )}
        {!added && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Textfarbe"><ColorField value={ov.color} fallback={info.color} onChange={(v) => set("color", v)} testid="element-color-input" /></Field>
              <Field label="Hintergrund"><ColorField value={ov.bg} fallback={info.bg} onChange={(v) => set("bg", v)} testid="element-bg-input" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Schriftgröße (px)">
                <Input type="number" value={ov.fontSize ?? ""} placeholder={String(Math.round(info.fontSize))} onChange={(e) => set("fontSize", e.target.value ? Number(e.target.value) : undefined)} data-testid="element-fontsize-input" />
              </Field>
              <Field label="Breite (px)">
                <Input type="number" value={ov.width ?? ""} placeholder="auto" onChange={(e) => set("width", e.target.value ? Number(e.target.value) : undefined)} data-testid="element-width-input" />
              </Field>
            </div>
            <Field label="Ausrichtung">
              <div className="flex gap-1">
                {[["left", "Links"], ["center", "Mitte"], ["right", "Rechts"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => set("align", ov.align === v ? undefined : v)}
                    className={`flex-1 py-1.5 text-xs font-bold border ${ov.align === v ? "bg-black text-[#EFDF00] border-black" : "border-[#D9D9D6] hover:border-black"}`}
                    data-testid={`element-align-${v}`}>{l}</button>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Verschiebung X"><Input type="number" value={ov.dx ?? 0} onChange={(e) => set("dx", Number(e.target.value) || undefined)} data-testid="element-dx-input" /></Field>
              <Field label="Verschiebung Y"><Input type="number" value={ov.dy ?? 0} onChange={(e) => set("dy", Number(e.target.value) || undefined)} data-testid="element-dy-input" /></Field>
            </div>
          </>
        )}
        <LayerField layer={layer} onLayer={onLayer} />
      </div>
      <div className="p-4 border-t border-[#E5E5E5] grid grid-cols-2 gap-2">
        {!added && (
          <button type="button" onClick={onParent} className="py-2 text-xs font-bold border border-[#D9D9D6] hover:border-black flex items-center justify-center gap-1" data-testid="element-parent-btn">
            <ArrowUpLeft className="w-3 h-3" /> Ganzer Bereich
          </button>
        )}
        {!added && (
          <button type="button" onClick={() => set("hidden", !ov.hidden || undefined)} className="py-2 text-xs font-bold border border-[#D9D9D6] hover:border-black flex items-center justify-center gap-1" data-testid="element-hide-btn">
            {ov.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {ov.hidden ? "Einblenden" : "Ausblenden"}
          </button>
        )}
        {!added && (
          <button type="button" onClick={onReset} className="py-2 text-xs font-bold border border-[#D9D9D6] hover:border-black flex items-center justify-center gap-1" data-testid="element-reset-btn">
            <RotateCcw className="w-3 h-3" /> Original
          </button>
        )}
        <button type="button" onClick={onDelete} className="py-2 text-xs font-bold bg-red-600 text-white hover:bg-red-700 flex items-center justify-center gap-1" data-testid="element-delete-btn">
          <Trash2 className="w-3 h-3" /> Löschen
        </button>
      </div>
    </aside>
  );
};
